import { useEffect, useState } from 'react'
import { ServerIcon, CpuChipIcon, ComputerDesktopIcon, CloudIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import AOS from 'aos'
import 'aos/dist/aos.css'

export default function Hero() {
  const [counts, setCounts] = useState({ projects: 0, clients: 0, satisfaction: 0 })

  useEffect(() => {
    AOS.init({ duration: 900, once: true, easing: 'ease-out-cubic' })

    const duration = 2000
    const steps = 60
    const targets = { projects: 150, clients: 80, satisfaction: 100 }
    let currentStep = 0

    const timer = setInterval(() => {
      currentStep++
      const progress = currentStep / steps
      setCounts({
        projects: Math.floor(targets.projects * progress),
        clients: Math.floor(targets.clients * progress),
        satisfaction: Math.floor(targets.satisfaction * progress),
      })
      if (currentStep >= steps) {
        clearInterval(timer)
        setCounts(targets)
      }
    }, duration / steps)

    return () => clearInterval(timer)
  }, [])

  return (
    <div className="relative bg-white overflow-hidden">

      {/* Dot grid */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: 'radial-gradient(#353E4014 1.5px, transparent 1.5px)',
          backgroundSize: '28px 28px',
        }}
      />
      {/* Fade edges */}
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,#07F2F20d,transparent)]" />

      {/* Hero content */}
      <div className="mx-auto max-w-5xl px-6 lg:px-8 pt-40 pb-24 text-center">

        {/* Headline */}
        <h1
          data-aos="fade-up"
          data-aos-delay="100"
          className="mt-8 text-5xl sm:text-6xl lg:text-7xl xl:text-[82px] font-bold tracking-tight text-adrox-dark leading-[1.08]"
        >
          Tecnología que<br />
          <span className="text-adrox-teal">impulsa</span> tu negocio.
        </h1>

        {/* Subtitle */}
        <p
          data-aos="fade-up"
          data-aos-delay="200"
          className="mt-8 text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed"
        >
          Desarrollamos software personalizado, implementamos IA y brindamos soporte técnico integral.
          Más de 150 proyectos entregados en Perú y Latinoamérica.
        </p>

        {/* CTAs */}
        <div
          data-aos="fade-up"
          data-aos-delay="300"
          className="mt-10 flex items-center justify-center gap-4 flex-wrap"
        >
          <a
            href="https://wa.me/+51933534235?text=Hola%20estoy%20interesado%20en%20adrox"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-xl bg-adrox-teal px-8 py-4 text-base font-semibold text-white hover:bg-adrox-teal-soft transition-all duration-200 hover:scale-[1.03] shadow-sm"
          >
            Empezar proyecto →
          </a>
          <a
            href="/nosotros"
            className="rounded-xl px-8 py-4 text-base font-semibold text-adrox-dark border border-gray-200 hover:border-adrox-teal/40 hover:bg-adrox-light transition-all duration-200"
          >
            Quiénes somos
          </a>
        </div>

        {/* Stats */}
        <div
          data-aos="fade-up"
          data-aos-delay="400"
          className="mt-20 flex items-center justify-center gap-8 sm:gap-16 flex-wrap"
        >
          <div className="text-center">
            <div className="text-4xl sm:text-5xl font-bold text-adrox-dark">{counts.projects}+</div>
            <div className="text-sm text-gray-400 mt-2 font-medium tracking-wide uppercase">Proyectos</div>
          </div>
          <div className="h-10 w-px bg-gray-100 hidden sm:block" />
          <div className="text-center">
            <div className="text-4xl sm:text-5xl font-bold text-adrox-dark">{counts.clients}+</div>
            <div className="text-sm text-gray-400 mt-2 font-medium tracking-wide uppercase">Clientes</div>
          </div>
          <div className="h-10 w-px bg-gray-100 hidden sm:block" />
          <div className="text-center">
            <div className="text-4xl sm:text-5xl font-bold text-adrox-dark">{counts.satisfaction}%</div>
            <div className="text-sm text-gray-400 mt-2 font-medium tracking-wide uppercase">Satisfacción</div>
          </div>
        </div>
      </div>

      {/* Tech logos carousel */}
      <div className="border-t border-gray-100 py-10 overflow-hidden">
        <p className="text-center text-xs font-semibold text-gray-400 mb-8 tracking-[0.2em] uppercase">
          Tecnologías que usamos
        </p>
        <div className="relative flex">
          <div className="flex animate-scroll items-center">
            {[1, 2].map((dupe) => (
              <div key={dupe} className="flex items-center space-x-12 sm:space-x-20 pr-12 sm:pr-20 shrink-0">
                <img src="/images/odoo.png" alt="Odoo" className="h-8 sm:h-10 w-auto object-contain opacity-40 hover:opacity-80 transition-opacity shrink-0" />
                <img src="/images/django.svg" alt="Django" className="h-8 sm:h-10 w-auto object-contain opacity-40 hover:opacity-80 transition-opacity shrink-0" />
                <img src="/images/react.svg" alt="React" className="h-8 sm:h-10 w-auto object-contain opacity-40 hover:opacity-80 transition-opacity shrink-0" />
                <img src="/images/fastapi.svg" alt="FastAPI" className="h-8 sm:h-10 w-auto object-contain opacity-40 hover:opacity-80 transition-opacity shrink-0" />
                <img src="/images/spring.svg" alt="Spring" className="h-8 sm:h-10 w-auto object-contain opacity-40 hover:opacity-80 transition-opacity shrink-0" />
                <ServerIcon className="h-8 w-8 sm:h-10 sm:w-10 text-gray-300 hover:text-adrox-teal transition-colors shrink-0" />
                <CpuChipIcon className="h-8 w-8 sm:h-10 sm:w-10 text-gray-300 hover:text-adrox-teal transition-colors shrink-0" />
                <ComputerDesktopIcon className="h-8 w-8 sm:h-10 sm:w-10 text-gray-300 hover:text-adrox-teal transition-colors shrink-0" />
                <CloudIcon className="h-8 w-8 sm:h-10 sm:w-10 text-gray-300 hover:text-adrox-teal transition-colors shrink-0" />
                <WrenchScrewdriverIcon className="h-8 w-8 sm:h-10 sm:w-10 text-gray-300 hover:text-adrox-teal transition-colors shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
